<template>
  <Campaign v-if="!isLoading && state && state.config" :key="state.config.campaign_id" :data="state" />
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { ConfigData } from '@/Config';
import Campaign from '@/components/Campaign.vue';
import { CampaignData } from '@/typings/interfaces/data/campaign';
import { useStore } from '@/store';
import { recursivelyWaitForPromises } from '@/utilities/virtualDom';
import useAxios from '@/hooks/useAxios';

export default defineComponent({
  name: 'CampaignPopupView',
  components: {
    Campaign
  },
  async serverPrefetch() {
    await this.readyPromise;
  },
  setup() {
    const route = useRoute();
    const store = useStore();

    const state = ref<CampaignData>((store.getters['campaign/data'] || {}) as CampaignData);
    const isLoading = ref(!store.getters['campaign/data']);

    const getCampaignData = async () => {
      if (typeof route.params.hash !== 'string') {
        return;
      }

      const { fetchData } = useAxios<CampaignData>(
        `${ConfigData.apiEndpoint}/api/v1/campaign/view/${route.params.campaignId}/${route.params.hash}/`,
        {
          vue: 1
        }
      );

      try {
        const response = await fetchData();

        if (response) {
          store.commit('campaign/setData', response);
          state.value = response;
        }
      } catch (e) {
        // TODO: Implement some sort of error handling for when campaign cannot be loaded
      }

      isLoading.value = false;
    };

    let readyPromiseResolve: (() => void) | undefined;

    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    const currentInstance = getCurrentInstance();

    nextTick(async () => {
      if (isLoading.value) {
        await getCampaignData();
      }

      // Run 2 passes of onBeforeEnter() awaits for components recursively down in the child tree.
      // This is to allow existing components initially rendered to be ready but also dynamically loaded
      // components that gets loaded in the first initial render.
      if (currentInstance?.vnode) {
        await recursivelyWaitForPromises(currentInstance.vnode, 'onBeforeEnter', 3);
      }

      if (readyPromiseResolve) {
        readyPromiseResolve();
      }
    });

    return {
      state,
      isLoading,
      getCampaignData,
      readyPromise
    };
  }
});
</script>
